/** The repeated change-management visuals: workflow state, approval decision,
 *  CAB outcome and risk.
 *
 *  State, decision and outcome share one map rather than three, because they
 *  share most of their vocabulary — `approved` and `rejected` mean the same
 *  thing on an RFC, on an approval and on an agenda item, and should not be
 *  three different greens.
 */
import { Badge } from '../../components/ui/badge'
import { cn } from '../../lib/utils'

type Variant = 'default' | 'neutral' | 'success' | 'warning' | 'critical' | 'info'

const WORKFLOW: Record<string, { label: string; variant: Variant }> = {
  // ChangeRequest.ChangeState
  draft: { label: 'Draft', variant: 'neutral' },
  submitted: { label: 'Submitted', variant: 'info' },
  assessment: { label: 'In assessment', variant: 'warning' },
  approved: { label: 'Approved', variant: 'success' },
  scheduled: { label: 'Scheduled', variant: 'info' },
  implementing: { label: 'Implementing', variant: 'default' },
  review: { label: 'In review', variant: 'default' },
  closed: { label: 'Closed', variant: 'neutral' },
  rejected: { label: 'Rejected', variant: 'critical' },
  failed: { label: 'Failed', variant: 'critical' },
  rolled_back: { label: 'Rolled back', variant: 'critical' },

  // Approval.Decision and CabAgendaItem.Outcome
  pending: { label: 'Pending', variant: 'neutral' },
  deferred: { label: 'Deferred', variant: 'warning' },

  // ChangeRequest.Outcome
  successful: { label: 'Successful', variant: 'success' },
  successful_with_issues: { label: 'Successful with issues', variant: 'warning' },
}

/** A workflow state, approval decision or CAB outcome. */
export function StatePill({ value }: { value: string }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>
  const style = WORKFLOW[value] ?? { label: value.replace(/_/g, ' '), variant: 'neutral' as const }
  return (
    <Badge variant={style.variant} size="sm">
      {style.label}
    </Badge>
  )
}

const RISK: Record<string, { label: string; className: string }> = {
  critical: {
    label: 'Critical',
    className: 'bg-critical/10 text-critical ring-1 ring-inset ring-critical/20',
  },
  high: { label: 'High', className: 'bg-warning/15 text-warning ring-1 ring-inset ring-warning/25' },
  medium: { label: 'Medium', className: 'bg-info/10 text-info ring-1 ring-inset ring-info/20' },
  low: { label: 'Low', className: 'bg-muted text-muted-foreground ring-1 ring-inset ring-border' },
}

/** Change risk and CI criticality — the same four-level scale. */
export function RiskPill({ value }: { value: string | null }) {
  if (!value) return <span className="text-xs text-muted-foreground">—</span>
  const style = RISK[value] ?? RISK.low
  return (
    <span
      className={cn(
        'inline-flex items-center whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-semibold',
        style.className,
      )}
    >
      {style.label}
    </span>
  )
}
